import angular from 'angular';
import { createError } from '../../error';

class RecapResponsePageController {
  constructor(
    $q,
    $scope,
    $anchorScroll,
    $timeout,
    $interval,
    $translate,
    showConfirmModal,
    LogService,
    APIService,
    RecapResponseService,
  ) {
    "ngInject";
    this.$q                        = $q;
    this.$scope                    = $scope;
    this.$anchorScroll             = $anchorScroll;
    this.$timeout                  = $timeout;
    this.$interval                 = $interval;
    this.$translate                = $translate;
    this.APIService                = APIService;
    this.showConfirmModal          = showConfirmModal;
    this.LogService                = LogService;
    this.RecapResponseService      = RecapResponseService;
    this._visibleItemsAndResponses = [];
    this._visibleFieldResponseIds  = [];
  }

  $onInit() {
    const AUTO_SAVE_PARTIAL_INTERVAL_SECONDS = 60;

    this._updateVisibleItemsAndResponses();

    this.$scope.$on('displayLogicUpdated', () => {
      this._updateVisibleItemsAndResponses();
    });

    this.$scope.$on('recapFieldTouched', () => {
      this.pageResponse.touch();
    });

    this.$scope.$on('scrollToFirstError', () => {
      this.validateAndScrollToFirstError();
    });

      // The autosave simply fires a partial response save at the specified interval
      // The timeout is cancelled when the user exits this page.
    this.partialAutoSaveInterval = this.$interval( () => {
      // Get the current values of all the form fields to determine if we need to do an autosave or not
      let currentVals = this.serializeCollectedValues(this.collectValues());
      if (angular.equals(currentVals, this.responseDataOnEntry)) {
          //console.info('No change - skipping');
      } else {
          // Update the comparison values for the next iteration and do the partial save
        this.responseDataOnEntry = currentVals;
        this.savePartialResponseAutomatic(this.pageResponse.recapResponse);
      }
    }, AUTO_SAVE_PARTIAL_INTERVAL_SECONDS * 1000);
  }

  $onDestroy() {
    this.cancelPartialSaveInterval();
  }

  get isOnline() {
    if (this.APIService) {
      return this.APIService.isOnline;
    }
    return false;
  }

  get pageResponse() {
    return this._pageResponse;
  }

  set pageResponse(pageResponse) {
    if (pageResponse !== this._pageResponse && this._pageResponse !== undefined) {
      this._pageResponse = pageResponse;
      this._updateVisibleItemsAndResponses();
    } else {
      this._pageResponse = pageResponse;
    }
  }

  get page() {
    return this.pageResponse && this.pageResponse.page || null;
  }

  get isFirstPage() {
    return this.pageResponse && this.pageResponse.isFirstPage || null;
  }

  get isLastPage() {
    return this.pageResponse && this.pageResponse.isLastPage || null;
  }

  get progress() {
    if (!this.page
      || !this.pageResponse
      || !this.pageResponse.recapResponse
      || !this.pageResponse.recapResponse.visiblePages
    ) {
      return 0;
    }
    const numPages = this.pageResponse.recapResponse.visiblePages.length;
    const idx      = this.pageResponse.recapResponse.indexOfVisiblePage(this.page);
    return Math.round((idx / numPages) * 100);
  }

  get visibleItemsAndResponses() {
    return this._visibleItemsAndResponses;
  }

  get showProgressBar() {
    return false;
  }

  _updateVisibleItemsAndResponses() {
    const newItems = this._getVisibleItemsAndResponses();
    if (!angular.equals(this._visibleItemsAndResponses, newItems)) {
      this._visibleItemsAndResponses = newItems;
    }
  }

  _getVisibleItemsAndResponses() {
    const itemsAndResponses       = [];
    const visibleFieldResponseIds = [];
    if (this.pageResponse && this.page) {
      for (let item of this.page.items) {
        if (!item.isForm) {
          // Error; we should only have forms now
          this.LogService.warn(`RecapResponsePageController._getVisibleItemsAndResponses() got non-form item on page`);
          continue;
        }
        const formResponseSet = this.pageResponse.getFormResponseSet(item);
        if (formResponseSet.visible) {
          itemsAndResponses.push({ item, response: formResponseSet });
          try {
            const formResponses  = formResponseSet.collectVisibleFormResponses();
            const fieldResponses = formResponses
              .map(formResponse => formResponse.collectVisibleFieldResponses())
              .reduce((pre, cur) => pre.concat(cur), []);
            fieldResponses.forEach(fieldResponse => visibleFieldResponseIds.push(fieldResponse.id));
          }
          catch (e) {
            this.LogService.error(`Error building list of visible field response ID's`);
          }
        }
      }
    }
    this._visibleFieldResponseIds = visibleFieldResponseIds;
    return itemsAndResponses;
  }

  validate() {
    return this.pageResponse && this.pageResponse.validate();
  }

  /**
   * Iterates through the recapResponse to get all the fields with their values.
   * Without this there's no way to know what the values of the fields actually are.
   *
   * The validate() call does this same work but triggers validation which shows up
   * on the page.  For autosaving we don't want to validate, we just want the data.
   */
  collectValues() {
    return this.pageResponse.collectValues();
  }

  /**
   * Extracts all the values from the field responses into a single array
   * so that comparisons can be done to detect changes for autosave of partials
   */
  serializeCollectedValues(values) {
    const vals = [];
    for (const x of values) {
        for (const y of x) {
           for (const z of y) {
               vals.push(z._value);
           }
        }
    }
    return vals;
  }

  nextPage() {
    this.pageResponse.touch();
    this.validate();
    this.$scope.$emit('nextPage', { pageResponse: this.pageResponse });
  }

  previousPage() {
    this.$scope.$emit('previousPage', {pageResponse: this.pageResponse});
  }

  finish() {
    // Prevent duplicate submissions
    if (this.isSaving) {
      this.LogService.warn("RecapResponsePageController.finish() save already in progress, ignoring");
      return this.$q.when(null);
    }
    const recapResponse = this.pageResponse.recapResponse;
    recapResponse.touchAllPages();
    this.LogService.log("RecapResponsePageController.finish()");
    return this.$q.when(this.cancelPartialSaveInterval())
      .then(() => {
        this.LogService.log("RecapResponsePageController.finish() cancelled partial save");
        if (this.onFinish) {
          this.LogService.log("RecapResponsePageController.finish() calling onFinish");
          return this.onFinish({
            recapResponse,
          });
        } else {
          this.LogService.error("RecapResponsePageController.finish() error: No onExit set");
        }
      });
  }

  exit() {
    return this.$translate('CONFIRM_EXIT_RECAP_SUBMISSION')
      .then(exitMessage => this.showConfirmModal({
        message: exitMessage,
        showAdditionalButton: true,
        additionalButtonText: 'SAVE_PARTIAL',
        additionalButtonAction: () => {
          this.LogService.log(`RecapResponsePageController.exit() calling this.savePartialResponse() before exit`);
          return this.savePartialResponse(this.pageResponse.recapResponse)
        }
      }))
      .then(() => {
        // Cancel the auto-save interval
        this.LogService.log(`RecapResponsePageController.exit() cancelling partial save interval then exiting`);
        return this.$q.when(this.cancelPartialSaveInterval())
          .then(() => this.doExit());
      });
  }

  doExit() {
    if (this.onExit) {
      this.LogService.log(`RecapResponsePageController.doExit() calling onExit()`);
      return this.onExit();
    } else {
      this.LogService.error("RecapResponsePageController.doExit(): No onExit set");
      throw createError({
        error: 'RecapResponsePageControllerNoOnExitSet',
        message: 'onExit handler not set for RecapResponsePage',
      });
    }
  }

  cancelPartialSaveInterval() {
   if (this.partialAutoSaveInterval) {
     this.$interval.cancel(this.partialAutoSaveInterval);
     this.partialAutoSaveInterval = null;
   }
  }

  savePartialResponseAndExit() {
    return this.$translate('CONFIRM_EXIT_SAVE_PARTIAL_RECAP_SUBMISSION')
      .then(exitMessage => this.showConfirmModal({
        message: exitMessage,
      }))
      .then(() => {
        this.LogService.log(`RecapResponsePageController.savePartialResponseAndExit()`);
        const recapResponse = this.pageResponse.recapResponse;
        return this.$q.when(this.cancelPartialSaveInterval())
          .then(() => this.savePartialResponse(recapResponse))
          .then(() => this.doExit());

      })
  }

  savePartialResponseAutomatic(recapResponse) {
    // We don't need the response, but we do need to run collectValues() so that pageResponse can access them.
    this.collectValues();
    if (!this.onSavePartialResponse) {
      this.LogService.error(`RecapResponsePageController.savePartialResponseAutomatic() this.onSavePartialResponse is missing!`);
      return false;
    }

    return this.onSavePartialResponse()
      .then(result => {
        this.LogService.info(`RecapResponsePageController.savePartialResponseAutomatic() got result:`, result);
        return result;
      })
      .catch(err => {
        this.LogService.error(`RecapResponsePageController.savePartialResponseAutomatic() got error:`, err);
        return this.$q.reject(err);
      });
  }

  savePartialResponse(recapResponse) {
    if (!this.onSavePartialResponse) {
      this.LogService.error(`RecapResponsePageController.savePartialResponse() this.onSavePartialResponse is missing!`);
      return false;
    }

    recapResponse.touchAllPages();
    const errors = this.validate();
    if (errors) {
      this.LogService.warn(`RecapResponsePagecontroller.savePartialResponse got validation errors:`, errors);
    }

    if (!this.onSavePartialResponse) {
      throw createError({
        error: 'RecapResponsePageControllerNoOnSavePartialResponse',
        message: 'onSavePartialResponse handler not set for RecapResponsePageController',
      });
    }

    return this.$q.when(this.onSavePartialResponse())
      .then(result => {
        this.LogService.info(`RecapResponsePageController.savePartialResponse() got result:`, result);
        return result;
      })
      .catch(err => {
        this.LogService.error(`RecapResponsePageController.savePartialResponse() got error:`, err);
        return this.$q.reject(err);
      });
  }

  validateAndScrollToFirstError() {
    const errors = this.pageResponse.validate();
    if (errors.length === 0) {
      return [];
    }

    this.LogService.debug("RecapResponsePageController.validateAndScrollToFirstError() got errors:", errors);

    this.$scope.$broadcast('expandFormsWithErrors');

    // Find "first" error by sorting errors by field response ID index in this._visibleFieldResponseIds
    const sortedErrors = errors.sort((error1, error2) => {
      const fieldResponseId1 = error1 && error1.fieldResponse && error1.fieldResponse.id;
      const fieldResponseId2 = error2 && error2.fieldResponse && error2.fieldResponse.id;
      const idx1 = this._visibleFieldResponseIds.indexOf(fieldResponseId1);
      const idx2 = this._visibleFieldResponseIds.indexOf(fieldResponseId2);
      return idx1 - idx2;
    });
    const firstError                = sortedErrors[0];
    const firstErrorFieldResponseId = firstError && firstError.fieldResponse && firstError.fieldResponse.id;
    const anchorName                = `fieldresponse_${firstErrorFieldResponseId}_label`;

    // Scroll to first erroneous response after timeout
    this.$timeout(() => {
      this.LogService.debug(`Scrolling to error with anchor ${anchorName}. Error:`, firstError);
      this.$anchorScroll(anchorName);
    }, 10);

    return errors;
  }
}

export default RecapResponsePageController;
